import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import type { AnswerValue, PublicQuizQuestion } from './types';

interface QuizQuestionProps {
  question: PublicQuizQuestion;
  answer: AnswerValue | undefined;
  onChange: (next: AnswerValue) => void;
  showError: boolean;
  errorExplanation?: string;
  questionNumber: number;
}

const QuizQuestion: React.FC<QuizQuestionProps> = ({
  question,
  answer,
  onChange,
  showError,
  errorExplanation,
  questionNumber,
}) => {
  const isMulti = question.type === 'multiSelect';

  const isOptionSelected = (id: string): boolean => {
    if (isMulti) return Array.isArray(answer) && answer.includes(id);
    return answer === id;
  };

  const handleSelect = (id: string) => {
    if (isMulti) {
      const current = Array.isArray(answer) ? answer : [];
      const next = current.includes(id) ? current.filter(o => o !== id) : [...current, id];
      onChange(next);
    } else {
      onChange(id);
    }
  };

  return (
    <motion.div
      className={`quiz${showError ? ' quiz--shake' : ''}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      <p className="quiz__prompt">
        <span className="quiz__prompt-icon" aria-hidden="true">Q{questionNumber}</span>
        <span>{question.prompt}</span>
      </p>
      <p className="quiz__hint">
        {isMulti ? 'Select all that apply' : 'Pick one'}
      </p>
      <div
        className="quiz__options"
        role={isMulti ? 'group' : 'radiogroup'}
        aria-label={question.prompt}
      >
        {question.options.map((option, idx) => {
          const selected = isOptionSelected(option.id);
          const className = [
            'quiz__option',
            selected && 'quiz__option--selected',
            showError && selected && 'quiz__option--wrong',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <motion.button
              type="button"
              key={option.id}
              className={className}
              onClick={() => handleSelect(option.id)}
              role={isMulti ? 'checkbox' : 'radio'}
              aria-checked={selected}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.22 + idx * 0.05, duration: 0.32, ease: 'easeOut' }}
            >
              <span
                className={[
                  'quiz__indicator',
                  isMulti && 'quiz__indicator--checkbox',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-hidden="true"
              >
                <AnimatePresence mode="wait">
                  {selected && (
                    isMulti ? (
                      <motion.svg
                        key="check"
                        className="quiz__check"
                        viewBox="0 0 12 12"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ duration: 0.18, ease: [0.34, 1.56, 0.64, 1] }}
                      >
                        <path d="M2 6.5 L5 9.5 L10 3.5" />
                      </motion.svg>
                    ) : (
                      <motion.span
                        key="dot"
                        className="quiz__dot"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.18, ease: [0.34, 1.56, 0.64, 1] }}
                      />
                    )
                  )}
                </AnimatePresence>
              </span>
              <span>{option.label}</span>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence>
        {showError && errorExplanation && (
          <motion.div
            className="quiz__explanation"
            role="alert"
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.32 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{errorExplanation}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default QuizQuestion;
